import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosClient from '../../../utils/axios';
import { resolve } from '../../../api/endPoints';

interface DemographicsData {
  data: any;
  isLoading: boolean;
  error: string | null;
}

const initialState: DemographicsData = {
  data: {},
  isLoading: true,
  error: null
};

const demographicsSlice = createSlice({
  name: 'demographics',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    setData: (state, action: PayloadAction<any>) => {
      state.data = action.payload;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    resetData: (state) => {
      state.data = {};
      state.error = null;
    }
  }
});

export default demographicsSlice.reducer;

export const { startLoading, stopLoading, setData, setError, resetData } =
  demographicsSlice.actions;

// Helper function to transform percentage data into chart data
const transformToChartData = (data: any[], labelKey: string = 'name') => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => ({
    name: item[labelKey] || item.name_eng || item.name || 'Unknown',
    value: item.percentage || 0,
    ...item
  }));
};

// Helper function to transform demographic data (array format)
const transformDemographicData = (data: any[]) => {
  if (!data || !Array.isArray(data)) return [];

  return transformToChartData(data);
};

// Helper function to transform cities data for map
const transformCitiesForMap = (citiesData: any[]) => {
  if (!citiesData || !Array.isArray(citiesData)) return [];

  return citiesData
    .filter((city) => city.lat && city.long)
    .map((city) => ({
      id: city.name,
      locationName: city.name,
      lat: city.lat,
      long: city.long,
      weight: city.percentage || 0
    }));
};

// Main function to extract and transform demographics data by platform
export const extractDemographicsData = (
  demographicsResponse: any,
  platform: string = '',
  t: any
) => {
  const platformData = platform ? demographicsResponse[platform] : null;
  const data = platformData?.data || {};

  const result: any = {
    platform
  };

  // Only include language data if it exists
  if (
    data.languages &&
    Array.isArray(data.languages) &&
    data.languages.length > 0
  ) {
    result.languageData = transformToChartData(data.languages, 'name_eng');
  } else {
    result.languageData = null;
  }

  // Only include age data if it exists
  if (
    data.age &&
    Array.isArray(data.age) &&
    data.age.length > 0
  ) {
    result.ageData = transformDemographicData(data.age);
  } else {
    result.ageData = null;
  }

  // Only include gender data if it exists
  if (
    data.gender &&
    Array.isArray(data.gender) &&
    data.gender.length > 0
  ) {
    result.genderData = transformDemographicData(data.gender);
  } else {
    result.genderData = null;
  }

  // Only include seniority data if it exists
  if (
    data.seniority &&
    Array.isArray(data.seniority) &&
    data.seniority.length > 0
  ) {
    result.seniorityData = transformDemographicData(data.seniority);
  } else {
    result.seniorityData = null;
  }

  // Only include industries data if it exists
  if (
    data.industries &&
    Array.isArray(data.industries) &&
    data.industries.length > 0
  ) {
    result.industriesData = transformToChartData(data.industries);
  } else {
    result.industriesData = null;
  }

  // Only include functions data if it exists
  if (
    data.functions &&
    Array.isArray(data.functions) &&
    data.functions.length > 0
  ) {
    result.functionsData = transformToChartData(data.functions);
  } else {
    result.functionsData = null;
  }

  // Only include company sizes data if it exists
  if (
    data.company_sizes &&
    Array.isArray(data.company_sizes) &&
    data.company_sizes.length > 0
  ) {
    result.companySizesData = transformToChartData(data.company_sizes);
  } else {
    result.companySizesData = null;
  }

  // Only include cities data if it exists
  if (data.cities && Array.isArray(data.cities) && data.cities.length > 0) {
    result.citiesData = {
      barChartData: transformToChartData(data.cities),
      mapData: transformCitiesForMap(data.cities)
    };
  } else {
    result.citiesData = null;
  }

  return result;
};

// Helper to get all available platforms from demographics data
export const getAvailablePlatforms = (demographicsResponse: any) => {
  if (!demographicsResponse || typeof demographicsResponse !== 'object')
    return [];

  return Object.keys(demographicsResponse).filter(
    (key) => demographicsResponse[key] && demographicsResponse[key].data
  );
};

// Helper to check if a platform has specific demographic data
export const hasDemographicData = (
  demographicsResponse: any,
  platform: string,
  dataType: string
) => {
  const platformData = demographicsResponse?.[platform]?.data;
  if (!platformData) return false;

  const data = platformData[dataType];
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === 'object' && data !== null)
    return Object.keys(data).length > 0;
  return false;
};

// Fetch demographics data based on location uuid
export const fetchDemographics = (locationUuid: string) => {
  return async (dispatch: any, getState: any) => {
    dispatch(startLoading());
    try {
      //get cache key from location details if available
      const cachekey = getState().locationDetails.cacheKey;
      const url = resolve(
        'analytics_demographics',
        {},
        { location__uuid: locationUuid, cache_key: cachekey }
      );
      const response = await axiosClient.get(url);
      dispatch(setData(response.data?.results?.[0]?.data || {}));
      dispatch(stopLoading());
    } catch (error: any) {
      console.error('Error fetching demographics data:', error);
      dispatch(setError(error.message || 'Failed to fetch demographics data'));
    }
  };
};
